import { useEffect, useRef, useState } from 'react';

const VIDEO_SRC = 'https://storage.helloreaddy.io/project_files/d30461bc-b051-4ed1-937c-7131f53a6467/3f7a5d60-a8fc-4716-a6d0-f438edfc906f_Senior_living_lifestyle_film_mon_20261012153153_.mp4';

/**
 * Video layer that sits over the existing Hero image inside the shared mask.
 * Becomes visible only after real playback produces a frame; on any failure
 * it stays transparent so the image remains the visible fallback.
 */
export default function HeroVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setEnabled(true);
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!enabled || !v) return undefined;
    let alive = true;
    let frameHandle = 0;

    const markReady = () => {
      if (alive) setReady(true);
    };
    const onPlaying = () => {
      // Wait for an actual rendered frame, not just the playing event.
      const rv = v as HTMLVideoElement & {
        requestVideoFrameCallback?: (cb: () => void) => number;
      };
      if (rv.requestVideoFrameCallback) {
        frameHandle = rv.requestVideoFrameCallback(markReady);
      } else {
        const check = () => {
          if (!alive) return;
          if (v.currentTime > 0 && v.readyState >= 2) markReady();
          else frameHandle = window.requestAnimationFrame(check);
        };
        check();
      }
    };
    const onError = () => {
      if (alive) setReady(false);
    };
    const onVisibility = () => {
      if (document.hidden) v.pause();
      else v.play().catch(() => {});
    };

    v.addEventListener('playing', onPlaying, { once: true });
    v.addEventListener('error', onError);
    document.addEventListener('visibilitychange', onVisibility);
    v.play().catch(() => {
      // Autoplay blocked: keep image visible.
    });

    return () => {
      alive = false;
      window.cancelAnimationFrame(frameHandle);
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('error', onError);
      document.removeEventListener('visibilitychange', onVisibility);
      v.pause();
      v.removeAttribute('src');
      v.load();
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <video
      ref={ref}
      className={`hero-media absolute inset-0 w-full h-full object-cover object-center pointer-events-none transition-opacity duration-300 ease-out ${ready ? 'opacity-100' : 'opacity-0'}`}
      src={VIDEO_SRC}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      controls={false}
      disablePictureInPicture
      aria-label="Daywell Living lifestyle film: everyday moments in senior living"
    />
  );
}